import React from "react";
import { Info, ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

import JourneyMessageScreen, {
  JourneyPillAction,
} from "@/b2b/components/JourneyMessageScreen";

/**
 * Where the tenant goes home from — where a partner has one for us to send them to.
 *
 * Only two partners define a destination, and the old screen showed "Back to Home" to everyone
 * regardless: for any other partner `redirectionUrl[partner]` was undefined, so the button
 * rendered, invited a tap, and did nothing. A missing button is honest; a dead one teaches the
 * tenant the page is broken.
 */
const PARTNER_HOME = {
  housr: (navigate) =>
    navigate(`/b/${localStorage.getItem("user_identifier")}?step=back-to-base`),
  xu: () => {
    window.location.href =
      "https://xulifestyle.com/zero-deposit/eligibility/failure";
  },
};

/**
 * Shown when a tenant is not eligible.
 *
 * The sibling of the two escalation screens and built on the same shell, but deliberately NOT an
 * escalation. Those two exist because the tenant is stuck on something of ours that only ops can
 * move; this is a decision that has already been made. Alerting the support channel on every
 * rejection would bury the tenants who genuinely need a human under people nobody can help — and
 * offering "talk to us" here implies the answer is negotiable, which is unkind when it is not.
 *
 * So it does one thing well: says what happened, in plain words, and offers the way out.
 *
 * The copy replaces "Uh Oh😕 / Good things are worth the wait. / Sorry you are not eligible." An
 * emoji headline makes a real financial rejection read as flippant, and "good things are worth
 * the wait" implies a wait that may never end. This states the outcome, avoids blaming them for
 * it, and leaves the door open without promising anything.
 */
export default function SorryScreen({ theme }) {
  const navigate = useNavigate();
  const partner = localStorage.getItem("partnerIdentifier");
  const goHome = PARTNER_HOME[(partner || "").toLowerCase()];

  return (
    <JourneyMessageScreen
      theme={theme}
      icon={
        <Info className={`h-6 w-6 ${theme?.headingColor}`} strokeWidth={1.75} />
      }
      title="We cannot approve this right now"
      body="We checked your details. We are not able to approve your rent application at this time. Thank you for trying, and sorry for the trouble."
    >
      {goHome && (
        <div className="mt-6 w-full">
          <JourneyPillAction
            theme={theme}
            onClick={() => goHome(navigate)}
            icon={<ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />}
            label="Back to home"
          />
        </div>
      )}
    </JourneyMessageScreen>
  );
}
